<template>
  <!-- WRAPPER -->
  <div class="wrapper" v-if="!loading">
    <!-- .blog-single -->
    <div class="readable-content blog-single" v-if="!!blogDetail">
      <!-- .hentry -->
      <article class="post type-post format-standard hentry">
        <!-- .entry-header -->
        <header class="entry-header">
          <h1 class="entry-title" v-html="blogDetail.mainTitle" />
        </header>
        <!-- .entry-header -->

        <!-- .entry-meta -->
        <div class="entry-meta" v-if="blogDetail.meta">
          <span class="posted-in" v-if="blogDetail.meta.mainCategory">
            posted in
            <a
              href="#"
              @click.stop.prevent
              :title="blogDetail.meta.mainCategory"
              v-html="blogDetail.meta.mainCategory"
            />
          </span>

          <span class="posted-on" v-if="blogDetail.meta.date">
            on
            <a
              href="#"
              @click.stop.prevent
              :title="blogDetail.meta.date"
              rel="bookmark"
            >
              <time class="entry-date" :datetime="blogDetail.meta.date"
                >November 9, 2012</time
              >
            </a>
          </span>

          <span class="posted-by" v-if="blogDetail.meta.authorName">
            by
            <a
              class="url fn n"
              href="#"
              @click.stop.prevent
              rel="author"
              :title="blogDetail.meta.authorName"
              v-html="blogDetail.meta.authorName"
            />
          </span>
        </div>
        <!-- .entry-meta -->

        <!-- .featured-image -->
        <div class="featured-image" v-if="blogDetail.mainImg">
          <img :src="blogDetail.mainImg" :alt="blogDetail.slug" />
        </div>
        <!-- .featured-image -->

        <div class="entry-content">
          <!-- .entry-content -->
          <template v-for="(detail, index) in blogDetail.content">
            <p
              :key="index.toString()"
              v-if="detail.type === 'html'"
              v-html="detail.data"
            />

            <div :key="index.toString()" v-if="detail.type === 'tagline'">
              <h3 v-html="detail.data" />
            </div>

            <pretty-print
              :key="index.toString()"
              v-if="detail.type === 'code'"
              :code="detail.data"
              :title="detail.title"
            />
          </template>
          <!-- .entry-content -->

          <!-- .project-action -->
          <div class="project-action" v-if="blogDetail.actions">
            <a
              v-for="(action, index) in blogDetail.actions"
              :key="index.toString()"
              href="#"
              :class="'button button-icon big ' + action.color"
            >
              <i :class="action.icon"></i>
              {{ action.name }}
            </a>
          </div>
          <!-- .project-action -->

          <!-- .related-posts -->
          <div class="related-posts" v-if="blogDetail.related">
            <h3>RELATED POSTS</h3>
            <ol>
              <li
                v-for="(relatedPost, index) in blogDetail.related"
                :key="index.toString()"
              >
                <a
                  :href="relatedPost.link"
                  rel="bookmark"
                  v-html="relatedPost.name"
                />
              </li>
            </ol>
          </div>
          <!-- .related-posts -->
        </div>

        <!-- .entry-meta -->
        <footer class="entry-meta" v-if="blogDetail.category">
          <h3>TAGS</h3>
          <a
            v-for="(category, index) in blogDetail.category"
            :key="index.toString()"
            rel="tags"
            v-html="category"
          />
        </footer>
        <!-- .entry-meta -->
      </article>
      <!-- .hentry -->

      <!-- .about-author -->
      <aside class="about-author" v-if="blogDetail.author">
        <h3>WRITTEN BY</h3>

        <!-- .author-bio -->
        <div class="author-bio">
          <!-- .author-img -->
          <div class="author-img">
            <a target="_blank" :href="blogDetail.author.link">
              <img
                v-if="blogDetail.author.img"
                :alt="blogDetail.author.name"
                :src="blogDetail.author.img"
                class="avatar"
              />
            </a>
          </div>
          <!-- .author-img -->

          <!-- .author-info -->
          <div class="author-info">
            <h4 class="author-name" v-html="blogDetail.author.name" />
            <p v-html="blogDetail.author.info" />
          </div>
          <!-- .author-info -->
        </div>
        <!-- .author-bio -->
      </aside>
      <!-- .about-author -->

      <!-- .nav-single -->
      <nav class="nav-single row" v-if="blogDetail.navigation">
        <div class="nav-previous col-sm-6" v-if="blogDetail.navigation.prev">
          <h4>PREVIOUS POST</h4>
          <a :href="blogDetail.navigation.prev.link" rel="prev">
            <span class="meta-nav">←</span>
            {{ blogDetail.navigation.prev.name }}
          </a>
        </div>

        <div class="nav-next col-sm-6" v-if="blogDetail.navigation.next">
          <h4>NEXT POST</h4>
          <a :href="blogDetail.navigation.next.link" rel="next">
            {{ blogDetail.navigation.next.name }}
            <span class="meta-nav">→</span>
          </a>
        </div>
      </nav>
      <!-- .nav-single -->

      <!-- #comments -->
      <div
        id="comments"
        v-if="blogDetail.allowComments && blogDetail.comments"
        class="comments-area"
      >
        <h2 class="comments-title">
          <i class="icon-chat"></i>
          {{ blogDetail.comments.total }} COMMENTS
        </h2>

        <!-- .commentlist -->
        <ol class="commentlist">
          <li
            class="comment thread"
            v-for="(comment, index) in blogDetail.comments.list"
            :class="{
              bypostauthor: comment.postByAuthor
            }"
            :key="index.toString()"
          >
            <!-- #comment-## -->
            <blog-comment v-bind="comment" />
            <!-- #comment-## -->
          </li>
        </ol>
        <!-- .commentlist -->

        <!-- #respond -->
        <div id="respond">
          <h2 id="reply-title">
            <i class="icon-comment"></i> DROP A COMMENT
            <small>
              <a
                rel="nofollow"
                href="#"
                id="cancel-comment-reply-link"
                @click.stop.prevent="resetCommentForm"
                v-if="formComments.commentId || formComments.name !== ''"
                >Cancel reply</a
              >
            </small>
          </h2>

          <!-- .commentform -->
          <form id="commentform" @submit.prevent="submitReply">
            <p class="comment-notes">
              Your email address will not be published. Required fields are
              marked
              <span class="required">*</span>
            </p>

            <p class="comment-form-author">
              <label for="author">
                Name
                <span class="required">*</span>
              </label>
              <input
                id="author"
                name="author"
                type="text"
                size="30"
                v-model="formComments.name"
                required
                aria-required="true"
              />
            </p>

            <p class="comment-form-email">
              <label for="email">
                Email
                <span class="required">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="text"
                size="30"
                v-model="formComments.email"
                required
                aria-required="true"
              />
            </p>

            <p class="comment-form-url">
              <label for="url">Website</label>
              <input
                id="url"
                name="url"
                type="text"
                size="30"
                v-model="formComments.website"
              />
            </p>

            <p class="comment-form-comment">
              <label for="comment">Comment</label>
              <textarea
                id="comment"
                name="comment"
                rows="8"
                v-model="formComments.message"
                required
                aria-required="true"
              ></textarea>
            </p>

            <!-- <p class="form-allowed-tags">
              You may use these
              <abbr title="HyperText Markup Language">HTML</abbr> tags and
              attributes:
              <code
                >&lt;a href="" title=""&gt; &lt;abbr title=""&gt; &lt;acronym
                title=""&gt; &lt;b&gt; &lt;blockquote cite=""&gt; &lt;cite&gt;
                &lt;code&gt; &lt;del datetime=""&gt; &lt;em&gt; &lt;i&gt; &lt;q
                cite=""&gt; &lt;strike&gt; &lt;strong&gt;</code
              >
            </p>-->

            <p class="form-submit">
              <input
                type="submit"
                name="submit"
                id="submit"
                class="button"
                :value="
                  formComments.commentId !== null
                    ? 'Reply To Comment'
                    : 'Post Comment'
                "
              />
            </p>
          </form>
          <!-- .commentform -->
        </div>
        <!-- #respond -->
      </div>
      <!-- #comments -->
    </div>
    <!-- .blog-single -->
  </div>
  <!-- WRAPPER -->
</template>
<script>
import BLOGBYSLUG from "@/json/blogBySlug";

export default {
  name: "Blog-By-Slug",
  beforeMount() {
    let slug = this.$route.params.blogSlug;
    // get Blog information via API
    this.$axios
      .get(`blog/${slug}`)
      .then(res => {
        this.blogDetail = res.data;
      })
      .catch(e => {
        console.log(e);
        // error while getting blog Details
      })
      .finally(() => {
        this.loading = false;
      });
  },
  created() {
    this.$eventBus.$on("comment-reply", this.replayToComment);
  },
  beforeDestroy() {
    this.$eventBus.$off("comment-reply");
  },
  data() {
    return {
      blogDetail: BLOGBYSLUG,
      formComments: {
        name: "",
        email: "",
        website: "",
        message: "",
        commentId: null
      },
      loading: true
    };
  },
  methods: {
    replayToComment(data) {
      this.formComments.commentId = data.commentId;
      // scoll to form
      setTimeout(() => {
        document
          .getElementById("cancel-comment-reply-link")
          .scrollIntoView({ behavioud: "smooth" });
      });
    },
    submitReply() {
      if (this.formComments.commentId) {
        // reply to comment
      } else {
        // new Comment
      }
      // add comment Id if replaying, else add As new
    },
    resetCommentForm() {
      this.formComments = {
        name: "",
        email: "",
        website: "",
        message: "",
        commentId: null
      };
    }
  }
};
</script>
<style scoped>
a:not([href]):not([tabindex]) {
  cursor: default;
}
a:not([href]):not([tabindex]):focus,
a:not([href]):not([tabindex]):hover {
  color: unset;
  border-color: transparent;
}
</style>
